import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, Toast } from './listPageStyles';
import { ListPageShell } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import './securityScreens.css';

// G-Y-10 — Etkin Yetki Görüntüleyici.
//
// KK-01 gereği ZORUNLUDUR: deny kaydı olmadığı için bir yetkinin nereden geldiği başka türlü
// izlenemez. "Bu kullanıcı bu ekranı neden görüyor?" sorusunun cevabı yalnız bu ekrandan verilir.
//
// Sonuç sunucudaki TEK çözümleyiciden (IPermissionResolver, §5.1) gelir — ekran kendi başına
// yetki hesaplamaz. Ayrı bir hesaplama yapsaydı, ekranın "açık" derken kapının "kapalı" demesi
// kaçınılmaz olurdu.

const PLATFORMS = [
  { value: 1, label: 'Web' },
  { value: 2, label: 'Android' },
  { value: 3, label: 'iOS' },
];

const REASON_TONE = {
  OK: 'ok',
  LICENSE_REQUIRED: 'danger',
  NO_SCREEN_RIGHT: 'danger',
  NO_ACTION_RIGHT: 'danger',
  PROFILE_FAIL_CLOSED: 'danger',
  EMPTY_DATA_SCOPE: 'warn',
};

const OUTCOME_TONE = (outcome) => {
  const o = String(outcome || '');
  if (o.startsWith('KAPALI') || o === 'GİZLİ') return 'danger';
  if (o === 'BOŞ') return 'warn';
  if (o === 'AÇIK' || o === 'GEÇTİ') return 'ok';
  return 'muted';
};

const VISIBILITY = { 0: 'Gizli', 1: 'Salt-okunur', 2: 'Düzenlenebilir' };
const VISIBILITY_TONE = { 0: 'danger', 1: 'warn', 2: 'ok' };

// PermissionContext.ACTION ile aynı sözlük.
const ACTIONS = [
  { value: '', label: 'Yalnız ekran erişimi' },
  { value: 1, label: 'Ekle' },
  { value: 2, label: 'Güncelle' },
  { value: 3, label: 'Sil' },
  { value: 4, label: 'Kopyala' },
  { value: 17, label: 'Görüntüle' },
];

export function EffectivePermissionsPage() {
  usePageMeta('Etkin Yetki Görüntüleyici', 'Kullanıcı & Yetki');

  const [users, setUsers] = useState([]);
  const [menus, setMenus] = useState([]);
  const [userId, setUserId] = useState('');
  const [menuId, setMenuId] = useState('');
  const [screenCode, setScreenCode] = useState('');
  const [actionTypeId, setActionTypeId] = useState('');
  const [platform, setPlatform] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  // Kullanıcı ve menü listeleri — hata halinde sessiz kalır, ekran yine elle Id ile çalışır.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const headers = authHeaders();
      const pull = async (url) => {
        try {
          const res = await fetch(url, { headers });
          if (!res.ok) return [];
          const j = await res.json();
          return j?.value || j?.data || j?.Data || (Array.isArray(j) ? j : []);
        } catch { return []; }
      };
      const [u, m] = await Promise.all([
        pull('/api/Users?$select=Id,FirstName,LastName,Email&$orderby=FirstName&$top=1000'),
        pull('/api/Menu/MenuTree'),
      ]);
      if (cancelled) return;
      setUsers(u.map((x) => ({
        id: x.id ?? x.Id,
        label: `${x.firstName ?? x.FirstName ?? ''} ${x.lastName ?? x.LastName ?? ''}`.trim()
               || (x.email ?? x.Email ?? ''),
      })).filter((x) => x.id));
      // Menü ağacı iç içe gelebilir — yalnız yol taşıyan yaprakları düzleştiriyoruz.
      const flat = [];
      const walk = (nodes) => (nodes || []).forEach((n) => {
        const id = n.id ?? n.Id;
        const path = n.path ?? n.Path;
        const title = n.title ?? n.Title ?? n.name ?? n.Name;
        if (id && path && path !== '#') flat.push({ id, label: `${title} (${id})` });
        walk(n.children ?? n.Children);
      });
      walk(m);
      setMenus(flat.sort((a, b) => a.label.localeCompare(b.label, 'tr')));
    })();
    return () => { cancelled = true; };
  }, []);

  const resolve = useCallback(async () => {
    if (!userId) { showToast('error', 'Kullanıcı seçiniz'); return; }
    if (platform === 1 && !menuId) { showToast('error', 'Web çözümlemesi için ekran seçiniz'); return; }
    if (platform !== 1 && !screenCode) { showToast('error', 'Mobil çözümleme için ekran kodu giriniz'); return; }

    setLoading(true);
    setResult(null);
    try {
      const qs = new URLSearchParams({ userId, platform: String(platform) });
      if (menuId) qs.set('menuId', menuId);
      if (screenCode) qs.set('screenCode', screenCode);
      if (actionTypeId) qs.set('actionTypeId', actionTypeId);

      const res = await fetch(`/api/permissions/effective?${qs}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Yetki çözümlenemedi');
      setResult(body?.data || body?.Data || null);
    } catch (e) {
      showToast('error', e.message || 'Yetki çözümlenemedi');
    } finally {
      setLoading(false);
    }
  }, [userId, menuId, screenCode, actionTypeId, platform]);

  const fieldEntries = Object.entries(result?.fieldVisibility || {});

  return (
    <div style={s.container}>
      <ListPageShell
        title="Etkin Yetki Görüntüleyici"
        subtitle="Bir kullanıcının bir ekranda ne yapabildiğini ve bu yetkinin hangi kuraldan geldiğini gösterir."
      >
        <div className="sec-card" style={{ marginBottom: 14 }}>
          <div className="sec-card-label">Sorgu</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginTop: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span className="sec-card-label">Kullanıcı</span>
              <select className="gp-select" value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">Seçiniz…</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span className="sec-card-label">Platform</span>
              <select className="gp-select" value={platform} onChange={(e) => setPlatform(Number(e.target.value))}>
                {PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </label>

            {platform === 1 ? (
              <>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span className="sec-card-label">Ekran</span>
                  <select className="gp-select" value={menuId} onChange={(e) => setMenuId(e.target.value)}>
                    <option value="">Seçiniz…</option>
                    {menus.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span className="sec-card-label">Aksiyon</span>
                  <select className="gp-select" value={actionTypeId} onChange={(e) => setActionTypeId(e.target.value)}>
                    {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </label>
              </>
            ) : (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span className="sec-card-label">Mobil ekran kodu</span>
                <span className="gp-num">
                  <input type="text" value={screenCode} placeholder="örn. ORDER"
                    onChange={(e) => setScreenCode(e.target.value)} />
                </span>
              </label>
            )}

            <button type="button" style={s.btnPrimary} disabled={loading} onClick={resolve}>
              {loading ? 'Çözümleniyor…' : 'Çözümle'}
            </button>
          </div>

          {platform !== 1 && (
            <div className="sec-card-hint" style={{ marginTop: 9 }}>
              Mobil yetkinin tek kaynağı mobil profildir; rol karışmaz. Profilde satırı olmayan ekran kapalı sayılır.
            </div>
          )}
        </div>

        {result && (
          <>
            <div className="sec-cards">
              <div className="sec-card">
                <div className="sec-card-label">Sonuç</div>
                <div style={{ marginTop: 8 }}>
                  <span className={`sec-badge sec-badge--${result.allowed ? 'ok' : 'danger'}`}>
                    {result.allowed ? 'ERİŞEBİLİR' : 'ERİŞEMEZ'}
                  </span>
                </div>
                <div className="sec-card-hint" style={{ marginTop: 7 }}>
                  <span className={`sec-badge sec-badge--${REASON_TONE[result.reasonCode] || 'muted'}`}>
                    {result.reasonCode}
                  </span>
                </div>
              </div>
              {result.message && (
                <div className="sec-card" style={{ flex: '2 1 340px' }}>
                  <div className="sec-card-label">Kullanıcıya gösterilecek mesaj</div>
                  <div className="sec-card-hint" style={{ marginTop: 7, lineHeight: 1.55 }}>{result.message}</div>
                </div>
              )}
            </div>

            <div className="sec-spec-group">Kural zinciri</div>
            <table className="c360-table" style={{ width: '100%' }}>
              <thead><tr><th style={{ width: 170 }}>Adım</th><th style={{ width: 150 }}>Sonuç</th><th>Gerekçe</th></tr></thead>
              <tbody>
                {(result.sourceChain || []).map((step, i) => (
                  <tr key={i}>
                    <td><strong>{step.step}</strong></td>
                    <td><span className={`sec-badge sec-badge--${OUTCOME_TONE(step.outcome)}`}>{step.outcome}</span></td>
                    <td>{step.detail}</td>
                  </tr>
                ))}
                {(result.sourceChain || []).length === 0 && (
                  <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20 }}>Zincir boş</td></tr>
                )}
              </tbody>
            </table>

            {fieldEntries.length > 0 && (
              <>
                <div className="sec-spec-group">Kolon görünürlüğü</div>
                <div className="sec-card-hint" style={{ marginBottom: 8 }}>
                  Birleşim kuralı: en geniş görünürlük kazanır. Listede olmayan alanlar düzenlenebilirdir.
                </div>
                <table className="c360-table" style={{ width: '100%' }}>
                  <thead><tr><th>Alan</th><th style={{ width: 180 }}>Görünürlük</th></tr></thead>
                  <tbody>
                    {fieldEntries.map(([key, vis]) => (
                      <tr key={key}>
                        <td><span className="c360-erp-chip">{key}</span></td>
                        <td><span className={`sec-badge sec-badge--${VISIBILITY_TONE[vis] || 'muted'}`}>
                          {VISIBILITY[vis] ?? vis}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </ListPageShell>
      <Toast toast={toast} />
    </div>
  );
}

export default EffectivePermissionsPage;
